// Support.js
import React, { useState } from 'react';
import { 
  Headphones, MessageSquare, Mail, Phone, Clock, Send,FileText, Video, Book, AlertCircle, 
  ChevronRight, User, Paperclip, X, Star
} from 'lucide-react';

const Support = () => {
  const [activeTab, setActiveTab] = useState('chat');
  const [ticketForm, setTicketForm] = useState({
    subject: '',
    category: '',
    priority: 'medium',
    message: '',
    attachment: null
  });
  const [chatMessage, setChatMessage] = useState('');
  const [chatMessages, setChatMessages] = useState([
    {
      id: 1,
      sender: 'support',
      message: 'Hello! Welcome to MediGo Support. How can I help you today?',
      time: '10:00 AM',
      avatar: 'S'
    }
  ]);
  const [isTyping, setIsTyping] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const supportCategories = [
    { value: 'technical', label: 'Technical Issue', icon: AlertCircle },
    { value: 'account', label: 'Account & Billing', icon: User },
    { value: 'medical', label: 'Medical Services', icon: FileText },
    { value: 'emergency', label: 'Emergency Support', icon: AlertCircle },
    { value: 'feedback', label: 'Feedback & Suggestions', icon: MessageSquare }
  ];

  const quickLinks = [
    { title: 'Getting Started Guide', icon: Book, link: '/guide/start' },
    { title: 'Video Tutorials', icon: Video, link: '/tutorials' },
    { title: 'FAQs', icon: FileText, link: '/help' },
    { title: 'Contact Information', icon: Phone, link: '/contact' }
  ];

  const commonIssues = [
    { 
      question: 'How do I book an online consultation?',
      answer: 'Go to "Find Doctors", select a doctor, choose your preferred time slot, and confirm your booking.'
    },
    { 
      question: 'My payment failed, what should I do?',
      answer: 'Please check your payment method and try again. If the issue persists, contact your bank or try an alternative payment method.'
    },
    { 
      question: 'How do I cancel an appointment?',
      answer: 'Go to "My Appointments", select the appointment you want to cancel, and click "Cancel Appointment". Cancellations made 2+ hours before are free.'
    },
    { 
      question: 'Where can I find my prescription?',
      answer: 'Your prescriptions are available in your profile under "Medical Records" > "Prescriptions".'
    },
    { 
      question: 'How do I update my medical information?',
      answer: 'Go to your Profile > Medical Info tab, click "Edit", update your information, and save the changes.'
    }
  ];

  const activeTickets = [
    {
      id: 'TKT-001',
      subject: 'Unable to join video consultation',
      status: 'in-progress',
      priority: 'high',
      created: '2 hours ago',
      lastUpdate: '30 minutes ago'
    },
    {
      id: 'TKT-002',
      subject: 'Refund request for cancelled appointment',
      status: 'pending',
      priority: 'medium',
      created: '1 day ago',
      lastUpdate: '1 day ago'
    }
  ];

  const handleSendMessage = () => {
    if (!chatMessage.trim()) return;

    const newMessage = {
      id: chatMessages.length + 1,
      sender: 'user',
      message: chatMessage,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      avatar: 'U'
    };

    setChatMessages([...chatMessages, newMessage]);
    setChatMessage('');
    setIsTyping(true);

    // Simulate support response
    setTimeout(() => {
      const supportResponse = {
        id: chatMessages.length + 2,
        sender: 'support',
        message: 'Thank you for your message. A support agent will assist you shortly. Your estimated wait time is 2-3 minutes.',
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        avatar: 'S'
      };
      setChatMessages(prev => [...prev, supportResponse]);
      setIsTyping(false);
    }, 2000);
  };

  const handleSubmitTicket = (e) => {
    e.preventDefault();
    // Handle ticket submission
    alert('Support ticket submitted successfully! We\'ll respond within 24 hours.');
    setTicketForm({
      subject: '',
      category: '',
      priority: 'medium',
      message: '',
      attachment: null
    });
  };

  const handleFileAttachment = (e) => {
    const file = e.target.files[0];
    if (file) {
      setTicketForm({ ...ticketForm, attachment: file });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'resolved': return 'bg-green-100 text-green-700';
      case 'in-progress': return 'bg-blue-100 text-blue-700';
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-red-600';
      case 'medium': return 'text-yellow-600';
      case 'low': return 'text-green-600';
      default: return 'text-gray-600';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <section className="bg-gradient-to-r from-primary-600 to-primary-700 text-white py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2 flex items-center">
                <Headphones className="w-8 h-8 mr-3" />
                Support Center
              </h1>
              <p className="text-primary-100">We're here to help 24/7</p>
            </div>
            <div className="hidden md:flex items-center space-x-6">
              <div className="flex items-center">
                <Phone className="w-5 h-5 mr-2" />
                <span>+254 797 310 641</span>
              </div>
              <div className="flex items-center">
                <Mail className="w-5 h-5 mr-2" />
                <span>support@medigo.co.ke</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-6 px-4 -mt-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {quickLinks.map((link, index) => {
              const Icon = link.icon;
              return (
                <a
                  key={index}
                  href={link.link}
                  className="bg-white rounded-lg shadow-md p-4 hover:shadow-lg transition flex flex-col items-center text-center"
                >
                  <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-3">
                    <Icon className="w-6 h-6 text-primary-600" />
                  </div>
                  <span className="text-sm font-medium text-gray-800">{link.title}</span>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            {/* Left Sidebar - Common Issues */}
            <div className="md:col-span-1">
              <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                <h3 className="text-lg font-bold mb-4 text-secondary-800">Common Issues</h3>
                <div className="space-y-3">
                  {commonIssues.map((issue, index) => (
                    <details key={index} className="group">
                      <summary className="cursor-pointer text-sm font-medium text-gray-700 hover:text-primary-600 flex items-center justify-between">
                        {issue.question}
                        <ChevronRight className="w-4 h-4 group-open:rotate-90 transition" />
                      </summary>
                      <p className="mt-2 text-sm text-gray-600 pl-4 border-l-2 border-primary-200">
                        {issue.answer}
                      </p>
                    </details>
                  ))}
                </div>
                <a href="/help" className="text-primary-600 text-sm font-medium hover:text-primary-700 mt-4 inline-block">
                  View All FAQs →
                </a>
              </div>

              {/* Active Tickets */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-bold mb-4 text-secondary-800">Your Active Tickets</h3>
                {activeTickets.length > 0 ? (
                  <div className="space-y-3">
                    {activeTickets.map((ticket) => (
                      <div key={ticket.id} className="border rounded-lg p-3 hover:bg-gray-50 transition cursor-pointer">
                        <div className="flex items-start justify-between mb-2">
                          <span className="text-xs font-semibold text-gray-500">{ticket.id}</span>
                          <span className={`text-xs font-medium px-2 py-1 rounded-full ${getStatusColor(ticket.status)}`}>
                            {ticket.status}
                          </span>
                        </div>
                        <p className="text-sm font-medium text-gray-800 mb-1">{ticket.subject}</p>
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <span className={getPriorityColor(ticket.priority)}>
                            {ticket.priority} priority
                          </span>
                          <span>Updated {ticket.lastUpdate}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-600">No active tickets</p>
                )}
              </div>
            </div>

            {/* Main Support Area */}
            <div className="md:col-span-2">
              <div className="bg-white rounded-lg shadow-md">
                {/* Tab Navigation */}
                <div className="border-b">
                  <div className="flex">
                    <button
                      onClick={() => setActiveTab('chat')}
                      className={`flex-1 px-6 py-4 text-center font-medium transition ${
                        activeTab === 'chat' 
                          ? 'text-primary-600 border-b-2 border-primary-600' 
                          : 'text-gray-600 hover:text-gray-800'
                      }`}
                    >
                      <MessageSquare className="w-5 h-5 inline mr-2" />
                      Live Chat
                    </button>
                    <button
                      onClick={() => setActiveTab('ticket')}
                      className={`flex-1 px-6 py-4 text-center font-medium transition ${
                        activeTab === 'ticket' 
                          ? 'text-primary-600 border-b-2 border-primary-600' 
                          : 'text-gray-600 hover:text-gray-800'
                      }`}
                    >
                      <FileText className="w-5 h-5 inline mr-2" />
                      Submit Ticket
                    </button>
                  </div>
                </div>

                {/* Live Chat Tab */}
                {activeTab === 'chat' && (
                  <div className="flex flex-col h-[600px]">
                    {/* Chat Header */}
                    <div className="p-4 border-b bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                          <span className="font-medium">Support Team</span>
                          <span className="text-sm text-gray-500 ml-2">Online</span>
                        </div>
                        <div className="flex items-center text-sm text-gray-500">
                          <Clock className="w-4 h-4 mr-1" />
                          Avg. response: 2 min
                        </div>
                      </div>
                    </div>

                    {/* Chat Messages */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-4">
                      {chatMessages.map((msg) => (
                        <div
                          key={msg.id}
                          className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                        >
                          <div className={`flex items-start max-w-xs ${msg.sender === 'user' ? 'flex-row-reverse' : ''}`}>
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                              msg.sender === 'user' ? 'bg-primary-600 ml-2' : 'bg-gray-300 mr-2'
                            } text-white text-sm font-semibold`}>
                              {msg.avatar}
                            </div>
                            <div>
                              <div className={`px-4 py-2 rounded-lg ${
                                msg.sender === 'user' 
                                  ? 'bg-primary-600 text-white' 
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {msg.message}
                              </div>
                              <span className="text-xs text-gray-500 mt-1 block">
                                {msg.time}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                      {isTyping && (
                        <div className="flex justify-start">
                          <div className="flex items-center bg-gray-100 px-4 py-2 rounded-lg">
                            <div className="flex space-x-1">
                              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Chat Input */}
                    <div className="p-4 border-t">
                      <div className="flex items-center space-x-2">
                        <button className="text-gray-500 hover:text-gray-700">
                          <Paperclip className="w-5 h-5" />
                        </button>
                        <input
                          type="text"
                          value={chatMessage}
                          onChange={(e) => setChatMessage(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                          placeholder="Type your message..."
                          className="flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                        <button
                          onClick={handleSendMessage}
                          className="bg-primary-600 text-white p-2 rounded-lg hover:bg-primary-700 transition"
                        >
                          <Send className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Submit Ticket Tab */}
                {activeTab === 'ticket' && (
                  <div className="p-6">
                    <form onSubmit={handleSubmitTicket} className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Subject *
                        </label>
                        <input
                          type="text"
                          value={ticketForm.subject}
                          onChange={(e) => setTicketForm({...ticketForm, subject: e.target.value})}
                          className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                          placeholder="Brief description of your issue"
                          required
                        />
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Category *
                          </label>
                          <select
                            value={ticketForm.category}
                            onChange={(e) => setTicketForm({...ticketForm, category: e.target.value})}
                            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                            required
                          >
                            <option value="">Select category</option>
                            {supportCategories.map(cat => (
                              <option key={cat.value} value={cat.value}>{cat.label}</option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Priority
                          </label>
                          <select
                            value={ticketForm.priority}
                            onChange={(e) => setTicketForm({...ticketForm, priority: e.target.value})}
                            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                          >
                            <option value="low">Low</option>
                            <option value="medium">Medium</option>
                            <option value="high">High</option>
                            <option value="urgent">Urgent</option>
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Detailed Description *
                        </label>
                        <textarea
                          value={ticketForm.message}
                          onChange={(e) => setTicketForm({...ticketForm, message: e.target.value})}
                          rows="6"
                          className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                          placeholder="Please provide as much detail as possible about your issue..."
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Attachment (Optional)
                        </label>
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                          {ticketForm.attachment ? (
                            <div className="flex items-center justify-between">
                              <div className="flex items-center">
                                <Paperclip className="w-5 h-5 text-gray-500 mr-2" />
                                <span className="text-sm text-gray-700">{ticketForm.attachment.name}</span>
                              </div>
                              <button
                                type="button"
                                onClick={() => setTicketForm({...ticketForm, attachment: null})}
                                className="text-red-600 hover:text-red-700"
                              >
                                <X className="w-5 h-5" />
                              </button>
                            </div>
                          ) : (
                            <label className="cursor-pointer text-center block">
                              <input
                                type="file"
                                onChange={handleFileAttachment}
                                className="hidden"
                                accept="image/*,.pdf,.doc,.docx"
                              />
                              <Paperclip className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                              <span className="text-sm text-gray-600">
                                Click to attach file (Max 10MB)
                              </span>
                            </label>
                          )}
                        </div>
                      </div>

                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex">
                          <AlertCircle className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0" />
                          <div className="text-sm text-blue-800">
                            <p className="font-semibold mb-1">Expected Response Time:</p>
                            <ul className="space-y-1">
                              <li>• Urgent: Within 2 hours</li>
                              <li>• High: Within 6 hours</li>
                              <li>• Medium: Within 24 hours</li>
                              <li>• Low: Within 48 hours</li>
                            </ul>
                          </div>
                        </div>
                      </div>

                      <button
                        type="submit"
                        className="w-full bg-primary-600 text-white py-3 rounded-lg font-semibold hover:bg-primary-700 transition"
                      >
                        Submit Support Ticket
                      </button>
                    </form>
                  </div>
                )}
              </div>

              {/* Contact Options */}
              <div className="mt-8 bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-bold mb-4 text-secondary-800">Other Ways to Reach Us</h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <a href="tel:+254797310641" className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
                    <Phone className="w-6 h-6 text-primary-600 mr-3" />
                    <div>
                      <p className="font-semibold text-gray-800">Phone</p>
                      <p className="text-sm text-gray-600">+254 797 310 641</p>
                    </div>
                  </a>
                  <a href="mailto:support@medigo.co.ke" className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
                    <Mail className="w-6 h-6 text-primary-600 mr-3" />
                    <div>
                      <p className="font-semibold text-gray-800">Email</p>
                      <p className="text-sm text-gray-600">support@medigo.co.ke</p>
                    </div>
                  </a>
                  <div className="flex items-center p-4 bg-gray-50 rounded-lg">
                    <Clock className="w-6 h-6 text-primary-600 mr-3" />
                    <div>
                      <p className="font-semibold text-gray-800">Hours</p>
                      <p className="text-sm text-gray-600">24/7 Support</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Satisfaction Survey */}
              <div className="mt-8 bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg p-6 text-white">
                <h3 className="text-lg font-bold mb-4">How Are We Doing?</h3>
                <p className="text-primary-100 mb-4">
                  Your feedback helps us improve our support services
                </p>
                <div className="flex items-center space-x-2">
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <button
                      key={rating}
                      className="w-10 h-10 bg-white/20 backdrop-blur rounded-lg hover:bg-white/30 transition flex items-center justify-center"
                    >
                      <Star className="w-5 h-5" />
                    </button>
                  ))}
                </div>
                <button className="mt-4 bg-white text-primary-600 px-6 py-2 rounded-lg font-semibold hover:bg-primary-50 transition">
                  Share Feedback
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Emergency Notice */}
      <section className="py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6">
            <div className="flex items-start">
              <AlertCircle className="w-6 h-6 text-red-600 mr-3 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-lg font-bold text-red-800 mb-2">Medical Emergency?</h3>
                <p className="text-red-700 mb-3">
                  If you're experiencing a medical emergency, please don't wait for support response.
                </p>
                <div className="flex flex-wrap gap-4">
                  <a
                    href="tel:911"
                    className="bg-red-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-red-700 transition inline-flex items-center"
                  >
                    <Phone className="w-5 h-5 mr-2" />
                    Call 911
                  </a>
                  <a
                    href="/emergency"
                    className="bg-white text-red-600 border-2 border-red-600 px-6 py-2 rounded-lg font-semibold hover:bg-red-50 transition inline-flex items-center"
                  >
                    Request Ambulance
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Support;